import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";

import { apiClient } from "~/api/ApiClient";
import { queryKeys } from "~/api/queryKeys";
import Breadcrumbs from "~/components/ui/breadcrumbs/Breadcrumbs";
import { PaginationConfig } from "~/components/ui/pagination/Pagination";
import VisibilityImprovementBanner from "~/components/visibilityMetrics/VisibilityImprovementBanner";
import VisibilityMetricsTable from "~/components/visibilityMetrics/VisibilityMetricsTable";
import { isEngineHealthy } from "~/components/visibilityPerformance/engineHealth";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { useSiteContext } from "~/contexts/SiteContext";
import { useTeam } from "~/contexts/TeamProvider";
import { parsePaginationHeaders } from "~/helpers/paginationHeaders";
import { usePageTitle } from "~/hooks/usePageTitle";
import { routes } from "~/router";

import { PagesResponseWithAuth } from "../PagesPage";
import styles from "../styles.module.scss";

const VisibilityEngineMetricsPage = () => {
  usePageTitle(PAGE_TITLES.visibilityMetrics);
  const { id: siteId } = useParams();
  const siteContext = useSiteContext();
  const site = siteContext?.site?.site ?? null;
  const { team, isLoading: isTeamLoading } = useTeam();

  if (!siteId) {
    throw new Error("No site id provided");
  }

  const fakeBreadcrumbs = [
    { title: PAGE_TITLES.dashboard, link: routes.home() },
    {
      title: PAGE_TITLES.visibilityMetrics,
      link: routes.site.visibilityMetrics({ id: siteId }),
    },
  ];

  const [pagination, setPagination] = useState<PaginationConfig>({
    currentPage: 1,
    itemsPerPage: 25,
    nextPage: null,
    prevPage: null,
    totalPages: 1,
    totalItems: 25,
  });

  const { data, isLoading, isFetching } = useQuery<PagesResponseWithAuth>({
    placeholderData: keepPreviousData,
    queryKey: queryKeys.pagesList(siteId, {
      pageSize: pagination.itemsPerPage,
      pageNumber: pagination.currentPage,
      sorting: [],
      searchValue: "",
      selectedStatuses: ["completed"],
      selectedPageTypes: ["all"],
      selectedCustomizations: ["all"],
      advancedConditions: [],
    }),
    queryFn: async () => {
      const response = await apiClient.sites.pagesDetail(Number(siteId), {
        q: { status_in: ["completed"] },
        "page[size]": pagination.itemsPerPage,
        "page[number]": pagination.currentPage,
      });
      const { totalCount, nextPage, prevPage, totalPages } =
        parsePaginationHeaders(response.headers);

      return {
        ...response.data,
        paginationMeta: {
          totalItems: totalCount,
          nextPage: nextPage ?? 0,
          prevPage: prevPage ?? 0,
          totalPages,
        },
      };
    },
  });

  useEffect(() => {
    if (!data?.paginationMeta) return;
    setPagination((prev) => ({ ...prev, ...data.paginationMeta }));
  }, [data?.paginationMeta]);

  const { data: visibilityReports } = useQuery({
    queryKey: queryKeys.siteVisibilityReports(site?.id),
    enabled: !!site?.id,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const response = await apiClient.sites.visibilityReportsDetail(site!.id);

      return response.data.site_visibility_reports;
    },
  });

  const desktopReport =
    visibilityReports?.find((r) => r.platform === "desktop") ?? null;

  // Gate access on the prerendering product entitlement. Wait for the team to
  // load before deciding, otherwise we'd bounce away before the data arrives.
  if (!isTeamLoading && !team?.subscription?.price?.product?.prerendering) {
    return <Navigate to={routes.site.root({ id: siteId })} replace />;
  }

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{PAGE_TITLES.visibilityMetrics}</h1>
      <Breadcrumbs fakeBreadcrumbs={fakeBreadcrumbs} />
      {desktopReport && (
        <VisibilityImprovementBanner
          report={desktopReport}
          variant={isEngineHealthy(site) ? "active" : "projection"}
        />
      )}
      <VisibilityMetricsTable
        site={site}
        data={data}
        pagination={pagination}
        setPagination={setPagination}
        isLoading={isLoading}
        isFetching={isFetching}
      />
    </div>
  );
};

export default VisibilityEngineMetricsPage;
